import {forceReload, httpStatus, sleep, timeout} from "../utils/index";
import config from "../../config";

// Livelog configuration from environment variables
const livelogConf = {
    ip: config.livelog.ip,
    port: config.livelog.port,
    key: config.livelog.key
};

window.bondResponses = window.bondResponses || {};
window.oneclickResponses = window.oneclickResponses || {};
window.rtlResponses = window.rtlResponses || {};


window.addEventListener("message", (event) => {
    if (event.data.action === "bondResponse") {
        let __palantirId = event.data.__palantirId;
        window.bondResponses[__palantirId] = {status: event.data.status, payload: event.data.payload};
    } else if (event.data.action === "oneclickResponse") {
        let __palantirId = event.data.__palantirId;
        window.oneclickResponses[__palantirId] = {"response": event.data.response};
    } else if (event.data.action === "rtlServerResponse") {
        let __palantirId = event.data.__palantirId;
        window.rtlResponses[__palantirId] = {"response": event.data.response};
    }
});


class BondApi {
    constructor(bondId, bondToken, apiType, jwtToken) {
        this.bondId = bondId;
        this.id = bondId;
        this.headers = new Headers();
        this.headers.append("Accept", "*/*");

        this.bondToken = bondToken || null;
        this.jwtToken = jwtToken || null;
        this.apiType = apiType || "cloud";
        this.baseApiPath = config.api.palantirApi;

        // Add JWT Authorization header for authenticated API requests
        if (this.jwtToken) {
            this.headers.append("Authorization", `Bearer ${this.jwtToken}`);
        }
    }

    async _logRequest(path, method, body, response) {
        let statusColor = response.status < 300 ? 'color: #198754' : 'color: #dc3545';
        console.groupCollapsed(`[${method}]\t${path}\t%c(${response.status} ${httpStatus[response.status]})`, statusColor);
        console.log(`[method]\t${method}`);
        console.log(`[path]\t\t${path}`);
        if (body) console.log(`[body]\t\t${body || ""}`);
        console.group(`Response < %c${response.status} ${httpStatus[response.status]}`, statusColor);
        try {
            const data = await response.json();
            console.log(data);
        } catch (e) {
            console.log({})
        }
        console.groupEnd();
        console.groupEnd();
    }

    setApiType(apiType) {
        this.apiType = apiType;
    }

    _request(path, method, body) {
        if (["POST", "PATCH", "PUT"].indexOf(method) > 0) body = body || "{}";
        const request = this.apiType === "cloud" ? this._cloud_request(path, method, body) : this._local_request(path, method, body);
        request.then((res) => {
            if (res) {
                this._logRequest(path, method, body, res.clone());
            }
        }).catch((error) => {
            console.error('Request failed:', path, error);
        });
        return request
    }

    _cloud_request(path, method, body) {
        let requestConfig = {
            method: method, headers: this.headers,
        };
        if (body) requestConfig.body = body;

        const bondRequest = new Request(this.baseApiPath + this.bondId + path, requestConfig);

        return timeout(4100, fetch(bondRequest).then((res) => {
            if (res.status === 401) throw Error("Unauthorized");
            if (res.status === 403) forceReload();
            return res.json().then((response) => {
                return new Response(JSON.stringify(response["b"]), {"status": response["s"]});
            }).catch((e) => {
                return new Response(null, {"status": res.status});
            })
        })).catch((e) => {
            console.warn(e);
        })
    }

    _local_request(path, method, body) {
        const reqId = crypto.randomUUID().replace(/-/g, '').slice(0, 10);
        window.postMessage({
            __palantirId: reqId,
            action: "bondRequest",
            bondId: this.bondId,
            token: this.bondToken,
            path: path,
            method: method,
            body: body
        }, "*");

        return new Promise((resolve, reject) => {
            let timeout = 4000;
            let stepSleep = 50;
            const checkResponse = () => {
                if (window.bondResponses[reqId]) {
                    const response = window.bondResponses[reqId];
                    delete window.bondResponses[reqId];
                    if (response.status === undefined) {
                        resolve(new Response(null, {"status": 408}));
                    } else {
                        try {
                            resolve(new Response(response.payload, {"status": response.status}));
                        } catch (e) {
                            resolve(new Response(null, {"status": response.status}));
                        }
                    }
                } else {
                    timeout -= stepSleep;
                    if (timeout <= 0) resolve(new Response(null, {"status": 408}));
                    setTimeout(checkResponse, stepSleep); // Check every 100ms
                }
            };
            checkResponse();
        });


    }

    get(path) {
        return this._request(path, "GET").then((res) => {
            return res.json().catch((e) => Promise.resolve({}));
        });
    }

    put(path, data) {
        return this._request(path, "PUT", JSON.stringify(data));
    }

    patch(path, data) {
        return this._request(path, "PATCH", JSON.stringify(data));
    }

    post(path, data) {
        return this._request(path, "POST", JSON.stringify(data));
    }

    delete(path) {
        return this._request(path, "DELETE", JSON.stringify({}));
    }

    request(path, data) {
        if (data) return this.patch(path, data);
        return this.get(path);
    }


    // BOND
    async deviceIds() {
        const _deviceIds = Object.keys(await this.get("/devices/"));
        return _deviceIds.filter(e => !e.startsWith("_"))
    }

    async devices(withState = false, withProperties = false) {
        let devices = {}

        let _deviceIds = await this.deviceIds();
        _deviceIds.map((deviceId) => {
            devices[deviceId] = new DeviceApi(this, deviceId);
        });

        for (let i = 0; i < Object.keys(devices).length; i += 10) {
            const deviceGroups = Object.keys(devices).slice(i, i + 10);
            await Promise.all(deviceGroups.map(async (deviceId) => {
                await devices[deviceId].reload(withState, withProperties);
            }));
        }

        return devices
    }


    action(actionPath, actionValue) {
        return this.put(actionPath, {argument: actionValue});
    }


    commandTx(commandPath) {
        return this.put(commandPath + "tx/", {});
    }


    // LOG LEVELS
    livelogOn() {
        let response = this.put("/debug/livelog/", livelogConf);
        if (response.status === 204) {
            return false;
        } else {
            return response
        }
    }

    livelogOff() {
        return fetch(this.baseApiPath + this.bondId + "/disconnect/", {
            headers: this.headers
        })
    }


    logLevels() {
        return this.get("/debug/syslog/");
    }

    setLogLevel(subsys, level) {
        if (subsys === 0) {
            subsys = null;
        }
        return this.patch("/debug/syslog/", {"lvl": parseInt(level), "subsys": subsys})
    }

    async scan(frequency, modulation) {
        let response = await this.put("/signal/scan/", {freq: frequency, modulation: modulation});
        if (response.status > 299) throw new Error("Failed to scan signal");
        while (true) {
            await sleep(500);
            response = await this.get("/signal/scan/");
            if (response.running) continue;
            if (response.success) {
                return await this.get("/signal/scan/signal/");
            } else return null
        }
    }


    tx(signal) {
        return this.put("/signal/tx/", {
            freq: signal.freq,
            modulation: signal.modulation,
            data: signal.data,
            encoding: signal.encoding,
            bps: signal.bps,
            reps: signal.reps
        });
    }

}

class DeviceApi {
    constructor(bondApi, deviceId) {
        this._bondApi = bondApi;
        this.deviceId = deviceId;
        this.data = null;
    }

    async reload(withState = false, withProperties = false) {
        this.data = await this.get()
        if (withState) this.data["state"] = await this.state();
        if (withProperties) this.data["properties"] = await this.properties();
        return this.data;
    }

    get() {
        return this._bondApi.get("/devices/" + this.deviceId);
    }

    patch(data) {
        return this._bondApi.patch("/devices/" + this.deviceId, data);
    }

    state(update) {
        return this._bondApi.request("/devices/" + this.deviceId + "/state", update);
    }

    properties(update) {
        return this._bondApi.request("/devices/" + this.deviceId + "/properties", update);
    }

    action(action, value) {
        return this._bondApi.put("/devices/" + this.deviceId + "/actions/" + action, {argument: value});
    }

}


export default BondApi;